/**
 * Downloads — every lesson video saved on this device, grouped by course.
 * Works fully offline: reads only the local registry.
 */
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useCallback, useMemo } from 'react';
import { Alert, Pressable, ScrollView, View } from 'react-native';
import { SizableText, XStack, YStack } from 'tamagui';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText, CardTitle, PageTitle, SectionTitle } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import {
  daysUntilExpiry,
  deleteAllDownloads,
  deleteDownload,
  formatBytes,
  formatDuration,
  getOfflinePlayability,
  pauseDownload,
  resumeDownload,
  retryDownload,
  useDownloads,
  useIsOnline,
  type DownloadRecord,
} from '@/lib/offline';
import { CONTENT_MAX_WIDTH, useIsMobile } from '@/lib/useDeviceType';

const EXPIRY_WARN_DAYS = 7;

function statusLine(record: DownloadRecord, online: boolean, userId: string | null): string {
  const percent =
    record.bytesTotal && record.bytesTotal > 0
      ? Math.min(99, Math.floor((record.bytesDownloaded / record.bytesTotal) * 100))
      : 0;
  switch (record.status) {
    case 'queued':
      return 'Preparing…';
    case 'downloading':
      return `Downloading ${percent}% · ${formatBytes(record.bytesDownloaded)} of ${formatBytes(record.bytesTotal)}`;
    case 'paused':
      return `Paused at ${percent}% · tap to resume`;
    case 'failed':
      return online ? `Failed · tap to retry${record.error ? ` (${record.error})` : ''}` : 'Failed · reconnect to retry';
    case 'complete': {
      const parts = [formatBytes(record.bytesDownloaded), formatDuration(record.durationSeconds)].filter(Boolean);
      const playability = getOfflinePlayability(record, userId);
      if (!playability.playable && playability.reason === 'not_entitled') parts.push('membership required');
      else if (!playability.playable && playability.reason === 'expired') parts.push('reconnect to keep watching');
      else {
        const days = daysUntilExpiry(record.lastVerifiedAt);
        if (!online && days <= EXPIRY_WARN_DAYS) parts.push(`offline access ends in ${days}d`);
      }
      if (record.hasNotation) parts.push('video only offline');
      return parts.join(' · ');
    }
  }
}

export default function DownloadsScreen() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const online = useIsOnline();
  const all = useDownloads();
  const userId = user?.id ?? null;

  const records = useMemo(() => all.filter((r) => !userId || r.userId === userId), [all, userId]);
  const groups = useMemo(() => {
    const byCourse = new Map<string, DownloadRecord[]>();
    for (const r of records) {
      const list = byCourse.get(r.courseTitle) ?? [];
      list.push(r);
      byCourse.set(r.courseTitle, list);
    }
    return [...byCourse.entries()].map(([title, items]) => ({
      title,
      items: items.sort((a, b) => a.lessonTitle.localeCompare(b.lessonTitle) || a.videoTitle.localeCompare(b.videoTitle)),
    }));
  }, [records]);

  const usedBytes = records.reduce((sum, r) => sum + (r.status === 'complete' ? r.bytesDownloaded : 0), 0);
  const completeCount = records.filter((r) => r.status === 'complete').length;

  const open = useCallback(
    (record: DownloadRecord) => {
      if (record.status === 'failed') {
        if (online) void retryDownload(record.lessonVideoId);
        return;
      }
      if (record.status === 'paused') {
        void resumeDownload(record.lessonVideoId);
        return;
      }
      if (record.status === 'downloading' || record.status === 'queued') {
        Alert.alert('Downloading…', undefined, [
          { text: 'Continue', style: 'cancel' },
          { text: 'Pause', onPress: () => void pauseDownload(record.lessonVideoId) },
        ]);
        return;
      }
      router.push({
        pathname: '/courses/[course-slug]/[lesson-slug]',
        params: {
          'course-slug': record.courseSlug,
          'lesson-slug': record.lessonSlug,
          ...(record.videoSlug ? { video: record.videoSlug } : {}),
        },
      } as never);
    },
    [router, online],
  );

  const remove = useCallback((record: DownloadRecord) => {
    Alert.alert('Remove download?', `${record.videoTitle} will be deleted from this device.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => void deleteDownload(record.lessonVideoId) },
    ]);
  }, []);

  // Permanent "…" on every row (agreed with Dan): the manage menu for that video.
  const showOptions = useCallback(
    (record: DownloadRecord) => {
      const actions: { text: string; style?: 'cancel' | 'destructive'; onPress?: () => void }[] = [];
      if (record.status === 'complete') actions.push({ text: 'Play', onPress: () => open(record) });
      if (record.status === 'downloading' || record.status === 'queued') {
        actions.push({ text: 'Pause download', onPress: () => void pauseDownload(record.lessonVideoId) });
      }
      if (record.status === 'paused') {
        actions.push({ text: 'Resume download', onPress: () => void resumeDownload(record.lessonVideoId) });
      }
      if (record.status === 'failed' && online) {
        actions.push({ text: 'Retry download', onPress: () => void retryDownload(record.lessonVideoId) });
      }
      actions.push({ text: 'Remove from device', style: 'destructive', onPress: () => remove(record) });
      actions.push({ text: 'Cancel', style: 'cancel' });
      Alert.alert(record.videoTitle, `${formatBytes(record.bytesDownloaded)} · ${record.courseTitle}`, actions);
    },
    [open, remove, online],
  );

  const removeAll = useCallback(() => {
    Alert.alert('Remove all downloads?', `Frees ${formatBytes(usedBytes)} on this device.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove all', style: 'destructive', onPress: () => void deleteAllDownloads() },
    ]);
  }, [usedBytes]);

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView contentContainerStyle={{ paddingBottom: isMobile ? 40 : 56 }} showsVerticalScrollIndicator={false}>
        <Container maxWidth={CONTENT_MAX_WIDTH} centered py={isMobile ? '$4' : '$6'}>
          <XStack ai="flex-end" jc="space-between" mb="$3">
            <YStack>
              <PageTitle color={C.text} ta="left">My downloads</PageTitle>
              <CaptionText color={C.muted}>
                {completeCount === 0
                  ? 'Nothing saved yet'
                  : `${completeCount} video${completeCount === 1 ? '' : 's'} · ${formatBytes(usedBytes)} on this device`}
              </CaptionText>
            </YStack>
            {records.length > 0 ? (
              <Pressable onPress={removeAll} hitSlop={8} accessibilityRole="button">
                <CaptionText color={C.error}>Remove all</CaptionText>
              </Pressable>
            ) : null}
          </XStack>

          {!online ? (
            <XStack ai="center" gap="$2" bg="#2B2B2B" br={8} px="$3" py="$2.5" mb="$3">
              <Ionicons name="cloud-offline-outline" size={18} color="#FFFFFF" />
              <SizableText color="#FFFFFF" fontSize={13} fontWeight="600" f={1}>
                You&apos;re offline. Saved videos below will play; everything else needs a connection.
              </SizableText>
            </XStack>
          ) : null}

          {records.length === 0 ? (
            <YStack ai="center" py="$8" gap="$3">
              <Ionicons name="download-outline" size={40} color={C.muted} />
              <BodyText color={C.muted} ta="center">
                Open any lesson and tap the download icon next to Share to watch it without internet.
              </BodyText>
            </YStack>
          ) : (
            <YStack gap="$4">
              {groups.map((group) => (
                <YStack key={group.title} gap="$2">
                  <SectionTitle color={C.text}>{group.title}</SectionTitle>
                  <YStack bg={C.cardBg} br={12} borderWidth={1} borderColor={C.border} overflow="hidden">
                    {group.items.map((record, index) => {
                      const playable = getOfflinePlayability(record, userId).playable;
                      const percent =
                        record.bytesTotal && record.bytesTotal > 0
                          ? Math.min(1, record.bytesDownloaded / record.bytesTotal)
                          : 0;
                      return (
                        <Pressable
                          key={record.lessonVideoId}
                          onPress={() => open(record)}
                          accessibilityRole="button"
                          style={({ pressed }) => ({
                            backgroundColor: pressed ? '#F4F1ED' : 'transparent',
                            borderTopWidth: index === 0 ? 0 : 1,
                            borderTopColor: C.border,
                          })}
                        >
                          <XStack ai="center" gap="$3" px="$3" py="$3">
                            <View
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 18,
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor:
                                  record.status === 'complete' ? (playable ? '#E6F2E8' : '#FDECEA') : '#EFEBE6',
                              }}
                            >
                              <Ionicons
                                name={
                                  record.status === 'complete'
                                    ? playable ? 'play' : 'lock-closed'
                                    : record.status === 'failed'
                                      ? 'refresh'
                                      : record.status === 'paused'
                                        ? 'pause'
                                        : 'download-outline'
                                }
                                size={18}
                                color={record.status === 'complete' ? (playable ? '#2E7D32' : '#B3261E') : C.text}
                              />
                            </View>
                            <YStack f={1} miw={0} gap={2}>
                              <CardTitle color={C.text} numberOfLines={2}>
                                {record.lessonTitle === record.videoTitle
                                  ? record.videoTitle
                                  : `${record.lessonTitle} · ${record.videoTitle}`}
                              </CardTitle>
                              <CaptionText color={C.muted} numberOfLines={2}>
                                {statusLine(record, online, userId)}
                              </CaptionText>
                              {record.status === 'downloading' || record.status === 'paused' ? (
                                <View style={{ height: 3, borderRadius: 2, backgroundColor: '#E0DED8', marginTop: 4 }}>
                                  <View
                                    style={{
                                      height: 3,
                                      borderRadius: 2,
                                      width: `${Math.round(percent * 100)}%`,
                                      backgroundColor: C.accent,
                                    }}
                                  />
                                </View>
                              ) : null}
                            </YStack>
                            <Pressable
                              onPress={() => showOptions(record)}
                              hitSlop={10}
                              accessibilityRole="button"
                              accessibilityLabel="Download options"
                            >
                              <Ionicons name="ellipsis-horizontal" size={22} color={C.muted} />
                            </Pressable>
                          </XStack>
                        </Pressable>
                      );
                    })}
                  </YStack>
                </YStack>
              ))}
            </YStack>
          )}
        </Container>
      </ScrollView>
    </YStack>
  );
}
